import { Outlet, useRouterState } from '@tanstack/react-router'
import { useEffect } from 'react'
import { RequireSession } from '@/app/auth'
import { PanelResizer, usePanel } from '@/app/panel'
import { MeetingPanel } from '@/features/meetings/MeetingPanel'
import { CommandPalette, useCommandPalette } from './CommandPalette'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'

/**
 * The session gate is here rather than around the router because `/share/$token`
 * hangs off the root and must stay reachable signed-out — it is opened from an
 * email by someone who has no account and never will.
 */
export function AppShell() {
  return (
    <RequireSession>
      <Shell />
    </RequireSession>
  )
}

function Shell() {
  const palette = useCommandPalette()
  const { isOpen, width, close } = usePanel()

  /**
   * Leaving the page collapses the panel.
   *
   * The panel is app-global so a citation can open a meeting without navigating
   * away from the conversation. Nothing else closes it, so a meeting opened
   * from a chat would otherwise follow the reader to Templates and Spaces,
   * taking half the window with it and belonging to neither.
   */
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  useEffect(close, [pathname, close])

  return (
    <div className="flex h-dvh overflow-hidden bg-[var(--color-canvas)]">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar />
        <div className="flex min-h-0 flex-1">
          <main className="min-w-0 flex-1 overflow-hidden">
            <Outlet />
          </main>
          {isOpen ? (
            <>
              <PanelResizer />
              <aside
                className="flex min-h-0 shrink-0 flex-col overflow-hidden"
                style={{ width }}
                aria-label="Meeting preview"
              >
                <MeetingPanel />
              </aside>
            </>
          ) : null}
        </div>
      </div>
      <CommandPalette open={palette.open} onOpenChange={palette.setOpen} />
    </div>
  )
}
